/**
 * Media Module
 *
 * Handles media library functionality including:
 * - File upload with drag & drop
 * - Media selection and bulk actions
 * - Media editing modal
 */

import { $, $$, ready } from '../utils/dom.js';
import { getToken } from '../utils/csrf.js';

// =============================================================================
// State
// =============================================================================

let selectedMedia = [];
let selectedFiles = [];
let currentEditingMediaId = null;

// =============================================================================
// Upload Modal
// =============================================================================

/**
 * Open upload modal
 */
export function openUploadModal() {
    const modal = $('#upload-modal');
    if (modal) modal.classList.remove('hidden');
}

/**
 * Close upload modal
 */
export function closeUploadModal() {
    const modal = $('#upload-modal');
    if (modal) modal.classList.add('hidden');

    selectedFiles = [];
    const preview = $('#files-preview');
    if (preview) preview.classList.add('hidden');

    const uploadBtn = $('#upload-btn');
    if (uploadBtn) uploadBtn.disabled = true;

    const fileInput = $('#file-input');
    if (fileInput) fileInput.value = '';
}

// =============================================================================
// Drag & Drop
// =============================================================================

/**
 * Initialize drag and drop functionality
 */
function initDragAndDrop() {
    const dropZone = $('#drop-zone');
    const fileInput = $('#file-input');

    if (!dropZone || !fileInput) return;

    dropZone.addEventListener('click', () => fileInput.click());

    dropZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropZone.classList.add('border-blue-500', 'bg-blue-50');
    });

    dropZone.addEventListener('dragleave', () => {
        dropZone.classList.remove('border-blue-500', 'bg-blue-50');
    });

    dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('border-blue-500', 'bg-blue-50');
        handleFiles(e.dataTransfer.files);
    });

    fileInput.addEventListener('change', (e) => {
        handleFiles(e.target.files);
    });
}

/**
 * Handle selected files
 * @param {FileList} files
 */
export function handleFiles(files) {
    selectedFiles = Array.from(files);
    displaySelectedFiles();

    const uploadBtn = $('#upload-btn');
    if (uploadBtn) uploadBtn.disabled = selectedFiles.length === 0;
}

/**
 * Display selected files preview
 */
function displaySelectedFiles() {
    const preview = $('#files-preview');
    const list = $('#files-list');

    if (!preview || !list) return;

    if (selectedFiles.length === 0) {
        preview.classList.add('hidden');
        return;
    }

    preview.classList.remove('hidden');
    list.innerHTML = '';

    selectedFiles.forEach((file) => {
        const div = document.createElement('div');
        div.className = 'flex items-center justify-between p-2 bg-zinc-50 rounded';
        div.innerHTML = `
            <span class="text-sm text-zinc-900">${file.name}</span>
            <span class="text-xs text-zinc-500">${formatFileSize(file.size)}</span>
        `;
        list.appendChild(div);
    });
}

/**
 * Format file size for display
 * @param {number} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Upload selected files
 */
export async function uploadFiles() {
    if (selectedFiles.length === 0) return;

    const uploadBtn = $('#upload-btn');
    const progressBar = $('#upload-progress');

    if (uploadBtn) {
        uploadBtn.disabled = true;
        uploadBtn.textContent = 'Uploading...';
    }

    if (progressBar) progressBar.classList.remove('hidden');

    const formData = new FormData();
    selectedFiles.forEach(file => {
        formData.append('files[]', file);
    });

    try {
        const response = await fetch('/media/upload', {
            method: 'POST',
            headers: {
                'X-CSRF-Token': getToken()
            },
            body: formData
        });

        const data = await response.json();

        if (data.success) {
            closeUploadModal();
            window.location.reload();
        } else {
            alert(data.message || 'Upload failed');
        }
    } catch (error) {
        console.error('Upload error:', error);
        alert('Upload failed. Please try again.');
    } finally {
        if (uploadBtn) {
            uploadBtn.disabled = false;
            uploadBtn.textContent = 'Upload';
        }
        if (progressBar) progressBar.classList.add('hidden');
    }
}

// =============================================================================
// Media Selection
// =============================================================================

/**
 * Toggle media selection
 * @param {string} mediaId
 * @param {HTMLElement} element
 */
export function toggleMediaSelection(mediaId, element) {
    const index = selectedMedia.indexOf(mediaId);

    if (index > -1) {
        selectedMedia.splice(index, 1);
        element.classList.remove('ring-2', 'ring-blue-500');
    } else {
        selectedMedia.push(mediaId);
        element.classList.add('ring-2', 'ring-blue-500');
    }

    updateBulkActionsUI();
}

/**
 * Update bulk actions UI based on selection
 */
function updateBulkActionsUI() {
    const bulkActions = $('#bulk-actions');
    const selectedCount = $('#selected-count');

    if (bulkActions) {
        if (selectedMedia.length > 0) {
            bulkActions.classList.remove('hidden');
        } else {
            bulkActions.classList.add('hidden');
        }
    }

    if (selectedCount) {
        selectedCount.textContent = selectedMedia.length;
    }
}

/**
 * Clear all selections
 */
export function clearSelection() {
    selectedMedia = [];
    $$('.media-item.ring-2').forEach(el => {
        el.classList.remove('ring-2', 'ring-blue-500');
    });
    updateBulkActionsUI();
}

/**
 * Delete selected media
 */
export async function deleteSelectedMedia() {
    if (selectedMedia.length === 0) return;

    if (!confirm(`Delete ${selectedMedia.length} media item(s)? This cannot be undone.`)) {
        return;
    }

    try {
        const response = await fetch('/media/bulk-delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getToken()
            },
            body: JSON.stringify({ ids: selectedMedia })
        });

        const data = await response.json();

        if (data.success) {
            window.location.reload();
        } else {
            alert(data.message || 'Delete failed');
        }
    } catch (error) {
        console.error('Delete error:', error);
        alert('Delete failed. Please try again.');
    }
}

// =============================================================================
// Media Edit Modal
// =============================================================================

/**
 * Open edit modal for a media item
 * @param {string} mediaId
 */
export async function openEditModal(mediaId) {
    currentEditingMediaId = mediaId;

    const modal = $('#edit-modal');
    const form = $('#edit-form');

    if (!modal || !form) return;

    modal.classList.remove('hidden');

    try {
        const response = await fetch(`/media/${mediaId}/details`);
        const data = await response.json();

        if (data.success) {
            $('#edit-title').value = data.media.title || '';
            $('#edit-alt').value = data.media.alt_text || '';
            $('#edit-caption').value = data.media.caption || '';
        }
    } catch (error) {
        console.error('Error loading media details:', error);
    }
}

/**
 * Close edit modal
 */
export function closeEditModal() {
    const modal = $('#edit-modal');
    if (modal) modal.classList.add('hidden');
    currentEditingMediaId = null;
}

/**
 * Save media edits
 */
export async function saveMediaEdit() {
    if (!currentEditingMediaId) return;

    const title = $('#edit-title')?.value;
    const altText = $('#edit-alt')?.value;
    const caption = $('#edit-caption')?.value;

    try {
        const response = await fetch(`/media/${currentEditingMediaId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getToken()
            },
            body: JSON.stringify({
                title,
                alt_text: altText,
                caption
            })
        });

        const data = await response.json();

        if (data.success) {
            closeEditModal();
            window.location.reload();
        } else {
            alert(data.message || 'Save failed');
        }
    } catch (error) {
        console.error('Save error:', error);
        alert('Save failed. Please try again.');
    }
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initMedia() {
    initDragAndDrop();
}

ready(initMedia);

// Expose globally
window.openUploadModal = openUploadModal;
window.closeUploadModal = closeUploadModal;
window.uploadFiles = uploadFiles;
window.handleFiles = handleFiles;
window.formatFileSize = formatFileSize;
window.toggleMediaSelection = toggleMediaSelection;
window.clearSelection = clearSelection;
window.deleteSelectedMedia = deleteSelectedMedia;
window.openEditModal = openEditModal;
window.closeEditModal = closeEditModal;
window.saveMediaEdit = saveMediaEdit;

export default {
    openUploadModal,
    closeUploadModal,
    uploadFiles,
    handleFiles,
    formatFileSize,
    toggleMediaSelection,
    clearSelection,
    deleteSelectedMedia,
    openEditModal,
    closeEditModal,
    saveMediaEdit
};
